import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  contentChild,
  forwardRef,
  inject,
  input,
  output,
  TemplateRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MultiSelectChangeEvent,
  MultiSelectModule,
  MultiSelectSelectAllChangeEvent,
} from 'primeng/multiselect';

/**
 * Wraps PrimeNG `p-multiselect` with required test identifiers and form integration.
 * Implements {@link ControlValueAccessor}; use {@code formControlName} or
 * {@code [formControl]} on this host inside a {@link FormGroup}.
 *
 * **Form fields (preferred)** — wrap in {@code app-agility-form-field-wrapper}.
 * Set {@link inputId} equal to the wrapper {@code fieldId} so
 * {@code app-form-field-label} associates the visible label with the control.
 * Prefer {@link ariaLabelledBy} when a visible label is present; use
 * {@link ariaLabel} for filter/toolbar usage without a visible label (WCAG 4.1.2).
 *
 * {@link inputId} and {@link dataTestId} are **required** on every usage.
 */
@Component({
  selector: 'app-agility-multiselect',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MultiSelectModule,
    NgTemplateOutlet,
  ],
  templateUrl: './agility-multiselect.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AgilityMultiselectComponent),
      multi: true,
    },
  ],
})
export class AgilityMultiselectComponent implements ControlValueAccessor {
  readonly selectedItemsTemplate = contentChild<
    TemplateRef<{ $implicit: unknown }>
  >('selectedItemsTemplate');

  readonly inputId = input.required<string>();
  readonly dataTestId = input.required<string>();
  readonly options = input.required<unknown[]>();

  /** Accessible name when no visible label is provided (e.g. filter/toolbar usage). */
  readonly ariaLabel = input<string>('');

  readonly optionLabel = input<string>('text');
  readonly optionValue = input<string | undefined>();
  readonly placeholder = input<string | undefined>();
  readonly showClear = input<boolean>(false);
  readonly filter = input<boolean>(false);
  readonly filterBy = input<string | undefined>();
  readonly resetFilterOnHide = input<boolean>(false);
  readonly appendTo = input<string | undefined>();
  readonly styleClass = input<string>('w-full');
  readonly readOnly = input<boolean>(false);
  readonly maxSelectedLabels = input<number | undefined>();
  readonly display = input<'comma' | 'chip'>('comma');
  readonly dropdownIcon = input<string | undefined>();
  readonly ariaLabelledBy = input<string | undefined>();
  readonly selectedItemsLabel = input<string | undefined>();
  readonly displaySelectedLabel = input<boolean>(true);
  readonly virtualScroll = input<boolean>(true);
  readonly virtualScrollItemSize = input<number | undefined>(36);
  readonly group = input<boolean>(false);
  readonly fluid = input<boolean>(false);

  readonly multiselectChange = output<MultiSelectChangeEvent>();
  readonly selectAllChange = output<MultiSelectSelectAllChangeEvent>();

  private readonly cdr = inject(ChangeDetectorRef);

  protected innerValue: unknown[] | null = null;
  private disabledFromForm = false;

  private onChange: (value: unknown[] | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  protected isDisabled(): boolean {
    return this.disabledFromForm || this.readOnly();
  }

  writeValue(value: unknown[] | null): void {
    this.innerValue = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: unknown[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledFromForm = isDisabled;
    this.cdr.markForCheck();
  }

  protected onSelectionChange(event: MultiSelectChangeEvent): void {
    this.innerValue = event.value;
    this.onChange(this.innerValue);
    this.onTouched();
    this.multiselectChange.emit(event);
  }

  protected onClear(): void {
    this.innerValue = [];
    this.onChange([]);
    this.onTouched();
  }

  protected onSelectAllChange(event: MultiSelectSelectAllChangeEvent): void {
    this.selectAllChange.emit(event);
  }
}
